import React, { useState, useEffect, useContext } from 'react';
import API_BASE_URL from '../../config';
import { getToken } from '../../utils/tokenUtils';
import RewardModal from './RewardModal';
import { WalletContext } from '../../context/WalletContext';
import { 
  Trophy, 
  Lock, 
  Gift, 
  Coins, 
  Ticket, 
  CheckCircle2, 
  Loader2, 
  AlertTriangle 
} from 'lucide-react';

const YourRewards = () => {
  const { refreshWallet } = useContext(WalletContext);
  const [rewards, setRewards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Modal state
  const [selectedReward, setSelectedReward] = useState(null);
  const [claimLoadingId, setClaimLoadingId] = useState(null);

  const fetchRewards = async () => {
    if (rewards.length === 0) {
      setLoading(true);
    }
    setError('');
    try {
      const rawToken = getToken();
      const response = await fetch(`${API_BASE_URL}/api/member/rewards`, {
        headers: {
          'Authorization': `Bearer ${rawToken}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to retrieve milestones and rewards.');
      }

      const data = await response.json();
      setRewards(data);
    } catch (err) {
      console.error(err);
      setError('Failed to fetch consistency rewards. Please refresh.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRewards();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedReward) {
      const updated = rewards.find(r => r.id === selectedReward.id);
      if (updated) {
        setSelectedReward(updated);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rewards]);

  const handleClaimDirect = async (rewardId) => {
    if (claimLoadingId) return;
    setClaimLoadingId(rewardId);
    try {
      const rawToken = getToken();
      const response = await fetch(`${API_BASE_URL}/api/member/rewards/${rewardId}/claim`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${rawToken}`,
          'Content-Type': 'application/json'
        }
      });
      if (response.ok) {
        refreshWallet();
        fetchRewards();
      } else {
        throw new Error('Claim request failed.');
      }
    } catch (err) {
      console.error(err);
      alert('Error claiming reward. Please try again.');
    } finally {
      setClaimLoadingId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-16 space-y-4">
        <Loader2 className="animate-spin text-[#6CD800]" size={32} />
        <p className="text-gray-400 text-xs">Syncing consistency progress...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Title */}
      <div>
        <h2 className="text-2xl font-bold text-white flex items-center gap-3">
          <Gift className="text-lime-400 animate-pulse" size={28} />
          <span>Milestone Progress Rewards</span>
        </h2>
        <p className="text-xs text-gray-400 mt-1">
          Complete scans at your gym to unlock special milestone scratch cards and claim your rewards.
        </p>
      </div>

      {error && (
        <div className="p-3 bg-red-500/10 border border-red-500/20 text-red-400 rounded-lg text-xs flex items-center gap-2">
          <AlertTriangle size={14} />
          <span>{error}</span>
        </div>
      )}

      {/* Grid of Milestone Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
        {rewards.map((reward) => {
          const { milestoneDay, isEarned, isScratched, isClaimed, resolvedRewardType, rewardValue } = reward;

          // Card State 1: Locked
          if (!isEarned) {
            return (
              <div 
                key={milestoneDay} 
                className="bg-[#141414] border border-gray-800/60 rounded-2xl p-6 flex flex-col items-center justify-between text-center min-h-[240px] opacity-50 relative group"
              >
                <div className="absolute top-4 right-4 bg-gray-900 border border-gray-800 text-gray-500 text-[9px] font-bold px-2 py-0.5 rounded-full">
                  Locked
                </div>
                
                <div className="h-12 w-12 rounded-full bg-gray-900 border border-gray-850 flex items-center justify-center text-gray-500">
                  <Lock size={20} />
                </div>

                <div className="space-y-1 mt-4">
                  <h4 className="text-lg font-bold text-gray-400 font-bebas tracking-wide">{milestoneDay}-Day Milestone</h4>
                  <p className="text-[10px] text-gray-500 leading-relaxed max-w-[180px] mx-auto">
                    Keep up scanning at your gym to unlock this scratch card!
                  </p>
                </div>

                <div className="w-full mt-6 bg-gray-900 border border-gray-800 py-2 rounded-lg text-xs font-semibold text-gray-600">
                  Scan to Unlock
                </div>
              </div>
            );
          }

          // Card State 2: Unscratched
          if (!isScratched) {
            return (
              <div 
                key={milestoneDay} 
                className="bg-gradient-to-br from-[#1c2912] to-[#0f140a] border border-[#6CD800]/20 rounded-2xl p-6 flex flex-col items-center justify-between text-center min-h-[240px] shadow-lg shadow-[#6CD800]/5 transition-all duration-300 hover:scale-[1.03] hover:border-[#6CD800]/40 relative"
              >
                <div className="absolute top-4 right-4 bg-[#6CD800]/25 text-[#6CD800] text-[9px] font-black tracking-wider uppercase px-2.5 py-0.5 rounded-full border border-[#6CD800]/25 animate-pulse">
                  Unlocked
                </div>

                <div className="h-12 w-12 rounded-full bg-[#6CD800]/10 border border-[#6CD800]/20 flex items-center justify-center text-[#6CD800]">
                  <Trophy size={20} />
                </div>

                <div className="space-y-1 mt-4">
                  <h4 className="text-lg font-bold text-white font-bebas tracking-wide">{milestoneDay}-Day Scratch Card</h4>
                  <p className="text-[10px] text-gray-400 leading-relaxed max-w-[180px] mx-auto">
                    Awesome job! Tap below to scratch this card and reveal your prize.
                  </p>
                </div>

                <button
                  onClick={() => setSelectedReward(reward)}
                  className="w-full mt-6 py-2 bg-[#6CD800] text-[#0B0F19] hover:bg-[#5bc200] rounded-lg text-xs font-black tracking-wider uppercase transition-colors"
                >
                  Scratch Card
                </button>
              </div>
            );
          }

          // Card State 3: Scratched but not claimed
          if (!isClaimed) {
            return (
              <div 
                key={milestoneDay} 
                className="bg-gradient-to-br from-[#1e232f] to-[#0f1118] border border-[#3b82f6]/20 rounded-2xl p-6 flex flex-col items-center justify-between text-center min-h-[240px] transition-all duration-300 hover:scale-[1.03] relative"
              >
                <div className="absolute top-4 right-4 bg-[#3b82f6]/20 text-[#3b82f6] text-[9px] font-black tracking-wider uppercase px-2 py-0.5 rounded-full border border-[#3b82f6]/20">
                  Revealed
                </div>

                <div className="h-12 w-12 rounded-full bg-[#3b82f6]/10 border border-[#3b82f6]/20 flex items-center justify-center text-[#3b82f6]">
                  {resolvedRewardType === 'Coins' ? <Coins size={20} /> : <Ticket size={20} />}
                </div>

                <div className="space-y-1 mt-4">
                  <h4 className="text-lg font-bold text-white font-bebas tracking-wide">{milestoneDay}-Day Reward</h4>
                  <div className="text-sm font-black text-[#6CD800] tracking-wide mt-1">
                    {resolvedRewardType === 'Coins' ? `🪙 ${rewardValue} Coins` : `🎟️ Promo Discount`}
                  </div>
                  <p className="text-[9px] text-gray-400 truncate max-w-[180px] mx-auto">
                    {resolvedRewardType === 'Coins' ? 'Claim to deposit into wallet' : `Code: ${rewardValue}`}
                  </p>
                </div>

                <button
                  onClick={() => handleClaimDirect(reward.id)}
                  disabled={claimLoadingId === reward.id}
                  className="w-full mt-6 py-2 bg-[#3b82f6] text-white hover:bg-[#2563eb] disabled:opacity-50 rounded-lg text-xs font-bold flex items-center justify-center gap-1.5 transition-colors"
                >
                  {claimLoadingId === reward.id && <Loader2 className="animate-spin" size={13} />}
                  Claim Reward
                </button>
              </div>
            );
          }

          // Card State 4: Claimed
          return (
            <div 
              key={milestoneDay} 
              className="bg-[#121212]/80 border border-gray-900 rounded-2xl p-6 flex flex-col items-center justify-between text-center min-h-[240px] opacity-75 relative"
            >
              <div className="absolute top-4 right-4 bg-emerald-500/10 text-emerald-400 text-[9px] font-bold px-2 py-0.5 rounded-full border border-emerald-500/20">
                Claimed
              </div>

              <div className="h-12 w-12 rounded-full bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center text-emerald-400">
                <CheckCircle2 size={20} />
              </div>

              <div className="space-y-1 mt-4">
                <h4 className="text-lg font-bold text-gray-400 font-bebas tracking-wide">{milestoneDay}-Day Completed</h4>
                <div className="text-sm font-bold text-emerald-400">
                  {resolvedRewardType === 'Coins' ? `+${rewardValue} Coins` : 'Promo Redeemed'}
                </div>
                <p className="text-[9px] text-gray-500 truncate max-w-[180px] mx-auto">
                  {resolvedRewardType === 'Coins' ? 'Deposited in Wallet' : `Code: ${rewardValue}`}
                </p>
              </div>

              <div className="w-full mt-6 bg-gray-950 border border-gray-900 py-2 rounded-lg text-xs font-bold text-gray-500 flex items-center justify-center gap-1">
                <CheckCircle2 size={12} /> Reward Claimed
              </div>
            </div>
          );
        })}
      </div>

      {/* Reward modal for scratching */}
      {selectedReward && (
        <RewardModal 
          reward={selectedReward}
          onClose={() => setSelectedReward(null)}
          onRefresh={fetchRewards}
        />
      )}
    </div>
  );
};

export default YourRewards;
